import { useState } from "react";
import { toast } from "sonner";
import {
  RotateCcw, X, ChevronLeft, ChevronRight, CheckCircle2, Check,
  UtensilsCrossed, CalendarDays, ListChecks, Wallet,
  GraduationCap, ShoppingCart, Heart, Sparkles, Pencil, Plus
} from "lucide-react";

/* ═══════════════════════════════════════════════════════
   WEEKLY RESET — front-end mock
   Produces a simple, low-pressure weekly plan across the
   7 household sections. Pure client-side state for now;
   ready for backend integration later.
   ═══════════════════════════════════════════════════════ */

/* ── The 7 sections shown on the promo card ── */
export const WEEKLY_RESET_SECTIONS = [
  { id: "food",      label: "Food",            icon: UtensilsCrossed, color: "text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800" },
  { id: "calendar",  label: "Calendar",        icon: CalendarDays,    color: "text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800" },
  { id: "chores",    label: "Chores",          icon: ListChecks,      color: "text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800" },
  { id: "money",     label: "Money",           icon: Wallet,          color: "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800" },
  { id: "school",    label: "School / Kids",   icon: GraduationCap,   color: "text-violet-600 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 border-violet-100 dark:border-violet-800" },
  { id: "supplies",  label: "Supplies",        icon: ShoppingCart,    color: "text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800" },
  { id: "wellbeing", label: "Family wellbeing", icon: Heart,          color: "text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800" },
];

/* ── Step 1 mock events ── */
const MOCK_EVENTS = [
  { id: "e1", label: "Parent-teacher conference",  when: "Tuesday · 4:30 PM" },
  { id: "e2", label: "Max — dentist appointment",  when: "Tomorrow · 8:30 AM" },
  { id: "e3", label: "Netball game — Lily",        when: "Saturday · 10:00 AM" },
  { id: "e4", label: "Car service",                when: "Thursday · all day" },
  { id: "e5", label: "Family dinner with Nan",     when: "Sunday · 5:00 PM" },
  { id: "e6", label: "School trip permission due", when: "Thursday" },
];

/* ── Step 2 sorting checklist ── */
const SORTING_ITEMS = [
  { id: "meal-plan",   label: "Meal plan",    icon: UtensilsCrossed },
  { id: "groceries",   label: "Grocery list", icon: ShoppingCart },
  { id: "chores",      label: "Chores",       icon: ListChecks },
  { id: "school",      label: "School admin", icon: GraduationCap },
  { id: "bills",       label: "Bills",        icon: Wallet },
  { id: "appointments",label: "Appointments", icon: CalendarDays },
  { id: "laundry",     label: "Laundry",      icon: RotateCcw },
  { id: "transport",   label: "Transport",    icon: CalendarDays },
  { id: "supplies",    label: "Supplies",     icon: ShoppingCart },
  { id: "calm",        label: "Calm Mode",    icon: Heart },
];

/* ── Step 3 mock plan ── */
const SUGGESTED_PLAN = [
  { day: "Monday",   focus: "Quick dinner + school prep",          tone: "bg-amber-50 border-amber-100 dark:bg-amber-900/15 dark:border-amber-800" },
  { day: "Tuesday",  focus: "Grocery pickup + laundry",            tone: "bg-blue-50 border-blue-100 dark:bg-blue-900/15 dark:border-blue-800" },
  { day: "Wednesday",focus: "Chore reset",                         tone: "bg-violet-50 border-violet-100 dark:bg-violet-900/15 dark:border-violet-800" },
  { day: "Thursday", focus: "Bills check",                         tone: "bg-emerald-50 border-emerald-100 dark:bg-emerald-900/15 dark:border-emerald-800" },
  { day: "Friday",   focus: "Family night",                        tone: "bg-rose-50 border-rose-100 dark:bg-rose-900/15 dark:border-rose-800" },
  { day: "Weekend",  focus: "Deep clean one area only",            tone: "bg-teal-50 border-teal-100 dark:bg-teal-900/15 dark:border-teal-800" },
];

/* ═══════════════════════════════════════════════════════
   PROMO CARD
   ═══════════════════════════════════════════════════════ */

export function WeeklyResetCard({ onStart, compact = false }) {
  return (
    <section data-testid="weekly-reset-card">
      <div className={`rounded-[28px] border border-blue-100 dark:border-blue-800/50 bg-gradient-to-br from-blue-50/80 via-white to-violet-50/40 dark:from-blue-900/15 dark:via-slate-900/40 dark:to-violet-900/10 ${compact ? "p-5" : "p-6"} shadow-sm`}>
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
            <RotateCcw className="w-5 h-5 text-blue-600 dark:text-blue-300" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50">Weekly Reset</h2>
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 rounded-full px-2 py-0.5">
                <Sparkles className="w-2.5 h-2.5" strokeWidth={1.5} />
                Mock plan
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
              One simple plan for the week — food, calendar, chores, money, school, supplies and family wellbeing in a four-step guided flow.
            </p>
          </div>
        </div>

        {/* Section pills */}
        <div className="mt-4 flex flex-wrap gap-1.5">
          {WEEKLY_RESET_SECTIONS.map(s => {
            const SIcon = s.icon;
            return (
              <span
                key={s.id}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${s.color}`}
                data-testid={`weekly-reset-section-${s.id}`}
              >
                <SIcon className="w-3 h-3" strokeWidth={1.5} />
                {s.label}
              </span>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-5 flex items-center gap-3">
          <button
            onClick={onStart}
            className="inline-flex items-center gap-2 bg-[#1a4d3e] hover:bg-[#163f33] text-white font-bold text-sm px-5 py-2.5 rounded-2xl transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.97]"
            data-testid="start-weekly-reset-btn"
          >
            <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
            Start Weekly Reset
          </button>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 italic">Takes about 2 minutes.</p>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   4-STEP MODAL
   ═══════════════════════════════════════════════════════ */

export function WeeklyResetModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [selectedEvents, setSelectedEvents] = useState(() => new Set(["e1", "e2", "e3"]));
  const [selectedSorts, setSelectedSorts] = useState(() => new Set(["meal-plan", "groceries", "chores"]));

  if (!open) return null;

  const totalSteps = 4;
  const toggleSet = (setter) => (id) => setter(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleEvent = toggleSet(setSelectedEvents);
  const toggleSort = toggleSet(setSelectedSorts);

  const reset = () => { setStep(1); };
  const close = () => { onClose(); setTimeout(reset, 200); };

  const goNext = () => setStep(s => Math.min(s + 1, totalSteps));
  const goBack = () => setStep(s => Math.max(s - 1, 1));

  const confirm = (intent) => {
    const eventsCount = selectedEvents.size;
    const sortsCount = selectedSorts.size;
    if (intent === "add") {
      toast.success(`Weekly Reset added to Home OS — ${eventsCount} event${eventsCount === 1 ? "" : "s"} flagged, ${sortsCount} area${sortsCount === 1 ? "" : "s"} to sort, 6 day-plans staged.`);
    } else if (intent === "edit") {
      toast("Edit mode — back to Step 3 to tweak the plan.");
      setStep(3);
      return;
    } else {
      toast("Weekly Reset cancelled.");
    }
    close();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="weekly-reset-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} />

      {/* Modal */}
      <div className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-2xl font-home animate-fadeUp">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                <RotateCcw className="w-5 h-5 text-blue-600 dark:text-blue-300" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50">Weekly Reset</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Step {step} of {totalSteps} — quick and low-pressure.</p>
              </div>
            </div>
            <button onClick={close} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" data-testid="weekly-reset-close">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          {/* Step indicator */}
          <div className="mt-4 flex gap-1.5">
            {[1, 2, 3, 4].map(i => (
              <div
                key={i}
                className={`flex-1 h-1.5 rounded-full transition-colors ${i <= step ? "bg-[#1a4d3e] dark:bg-emerald-500" : "bg-gray-200 dark:bg-slate-700"}`}
                data-testid={`weekly-reset-step-indicator-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {step === 1 && <Step1 events={MOCK_EVENTS} selected={selectedEvents} onToggle={toggleEvent} />}
          {step === 2 && <Step2 items={SORTING_ITEMS} selected={selectedSorts} onToggle={toggleSort} />}
          {step === 3 && <Step3 plan={SUGGESTED_PLAN} selectedSorts={selectedSorts} />}
          {step === 4 && <Step4 eventCount={selectedEvents.size} sortCount={selectedSorts.size} />}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-6 py-4 border-t border-gray-100 dark:border-slate-800 flex items-center gap-2">
          {step > 1 && step < 4 && (
            <button
              onClick={goBack}
              className="inline-flex items-center gap-1 px-4 py-2.5 rounded-2xl text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="weekly-reset-back-btn"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />Back
            </button>
          )}

          {step < 4 && (
            <button
              onClick={goNext}
              className="ml-auto inline-flex items-center gap-1 bg-[#1a4d3e] hover:bg-[#163f33] text-white font-bold text-sm px-5 py-2.5 rounded-2xl transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.97]"
              data-testid="weekly-reset-next-btn"
            >
              {step === 3 ? "Confirm plan" : "Continue"}
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}

          {step === 4 && (
            <div className="flex flex-wrap items-center gap-2 w-full">
              <button
                onClick={() => confirm("add")}
                className="inline-flex items-center gap-1.5 bg-[#1a4d3e] hover:bg-[#163f33] text-white font-bold text-sm px-5 py-2.5 rounded-2xl transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.97]"
                data-testid="weekly-reset-add-btn"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />Add to Home OS
              </button>
              <button
                onClick={() => confirm("edit")}
                className="inline-flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 font-bold text-sm px-4 py-2.5 rounded-2xl hover:border-violet-300 dark:hover:border-violet-600 transition-colors"
                data-testid="weekly-reset-edit-btn"
              >
                <Pencil className="w-4 h-4" strokeWidth={1.5} />Edit plan
              </button>
              <button
                onClick={() => confirm("cancel")}
                className="ml-auto px-4 py-2.5 rounded-2xl text-sm font-bold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                data-testid="weekly-reset-cancel-btn"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Step 1 ── */
function Step1({ events, selected, onToggle }) {
  return (
    <div data-testid="weekly-reset-step-1">
      <h3 className="text-base font-extrabold text-gray-900 dark:text-gray-50">What is happening this week?</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Tick anything we should plan around.</p>
      <div className="space-y-2">
        {events.map(ev => {
          const checked = selected.has(ev.id);
          return (
            <button
              key={ev.id}
              onClick={() => onToggle(ev.id)}
              className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all duration-150 ${
                checked
                  ? "bg-[#1a4d3e]/5 dark:bg-emerald-900/20 border-[#1a4d3e]/40 dark:border-emerald-700"
                  : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600"
              }`}
              data-testid={`weekly-reset-event-${ev.id}`}
            >
              <Checkbox checked={checked} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">{ev.label}</p>
                <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{ev.when}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Step 2 ── */
function Step2({ items, selected, onToggle }) {
  return (
    <div data-testid="weekly-reset-step-2">
      <h3 className="text-base font-extrabold text-gray-900 dark:text-gray-50">What needs sorting?</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Pick the areas ARA should help with this week.</p>
      <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
        {items.map(item => {
          const checked = selected.has(item.id);
          const ItemIcon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => onToggle(item.id)}
              className={`flex items-center gap-2.5 rounded-2xl border px-3 py-2.5 text-left transition-all duration-150 ${
                checked
                  ? "bg-violet-50 dark:bg-violet-900/20 border-violet-300 dark:border-violet-700 text-violet-900 dark:text-violet-100"
                  : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 text-gray-700 dark:text-gray-200"
              }`}
              data-testid={`weekly-reset-sort-${item.id}`}
            >
              <Checkbox checked={checked} />
              <ItemIcon className="w-4 h-4 opacity-70" strokeWidth={1.5} />
              <span className="text-sm font-bold">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Step 3 ── */
function Step3({ plan, selectedSorts }) {
  return (
    <div data-testid="weekly-reset-step-3">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-violet-500" strokeWidth={1.5} />
        <h3 className="text-base font-extrabold text-gray-900 dark:text-gray-50">ARA's suggested plan</h3>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
        Built from what you flagged. Keep it light — only deep clean one area, not the whole house.
      </p>
      <div className="space-y-2">
        {plan.map((p, idx) => (
          <div key={p.day} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${p.tone}`} data-testid={`weekly-reset-plan-day-${idx}`}>
            <div className="w-10 text-center">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Day</p>
              <p className="text-xs font-extrabold text-gray-900 dark:text-gray-50">{idx + 1}</p>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50">{p.day}</p>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{p.focus}</p>
            </div>
          </div>
        ))}
      </div>
      {selectedSorts.size > 0 && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 italic">
          Tailored around: {[...selectedSorts].slice(0, 4).join(", ")}{selectedSorts.size > 4 ? "…" : ""}.
        </p>
      )}
    </div>
  );
}

/* ── Step 4 ── */
function Step4({ eventCount, sortCount }) {
  return (
    <div data-testid="weekly-reset-step-4" className="text-center py-2">
      <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
        <CheckCircle2 className="w-7 h-7 text-emerald-600 dark:text-emerald-300" strokeWidth={1.5} />
      </div>
      <h3 className="text-lg font-extrabold text-gray-900 dark:text-gray-50">Ready to lock it in?</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto leading-relaxed">
        ARA will stage the plan in Home OS. You can keep editing any day from the Calendar, Chores or Meals pages.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2 max-w-sm mx-auto">
        <SummaryStat label="Events flagged" value={eventCount} />
        <SummaryStat label="Areas to sort" value={sortCount} />
      </div>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-4 italic">
        Mock plan only — no calendar or task updates are sent yet.
      </p>
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 p-3" data-testid={`weekly-reset-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-2xl font-extrabold text-gray-900 dark:text-gray-50 tabular-nums">{value}</p>
      <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
    </div>
  );
}

function Checkbox({ checked }) {
  return (
    <span
      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
        checked
          ? "bg-[#1a4d3e] border-[#1a4d3e] dark:bg-emerald-500 dark:border-emerald-500 text-white"
          : "bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600"
      }`}
    >
      {checked && <Check className="w-3 h-3" strokeWidth={1.5} />}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════
   useWeeklyReset — convenience hook so any page can drop
   in <Card/> + <Modal/> with one import.
   ═══════════════════════════════════════════════════════ */

export function useWeeklyReset() {
  const [open, setOpen] = useState(false);
  const start = () => setOpen(true);
  const close = () => setOpen(false);
  return { open, start, close };
}
